/**
 * User-controlled "Allow Screenshots" preference.
 *
 * Source of truth lives in AsyncStorage so the same key works on iOS, Android,
 * and the React Native Web renderer (Electron desktop). Defaults OFF — the app
 * is HIPAA-safe out of the box; the user opts in.
 *
 * Coverage when allowed = true:
 *   • Mobile: `useScreenshotZone` skips `preventScreenCaptureAsync` and skips
 *     the screenshot-detected notice on Documents / Notes screens.
 *   • Electron desktop: main process turns off `setContentProtection` on the
 *     main window via an IPC bridge (renderer pushes here, main mirrors to
 *     `preferences.json` so the choice is applied at next launch BEFORE the
 *     renderer loads).
 *   • Web (plain browser): no-op — browsers can't block screenshots.
 *
 * Runtime subscribers are notified synchronously after each write so an open
 * screen (e.g. Notes) can re-arm or release its native block immediately.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { getElectronBridge } from './platform';

export const ALLOW_SCREENSHOTS_KEY = '@eleplan/allowScreenshots';

type Listener = (allowed: boolean) => void;

let cached: boolean | null = null;
let loadPromise: Promise<boolean> | null = null;
const listeners = new Set<Listener>();

async function load(): Promise<boolean> {
  if (cached !== null) return cached;
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const raw = await AsyncStorage.getItem(ALLOW_SCREENSHOTS_KEY);
      cached = raw === 'true';
    } catch {
      cached = false;
    }
    return cached;
  })();
  return loadPromise;
}

/** Async read. Resolves to the current value (false by default). */
export async function getAllowScreenshots(): Promise<boolean> {
  return load();
}

/**
 * Synchronous read of the in-memory cache. Returns `null` if the value hasn't
 * been loaded yet. Callers that need an exact value should `await
 * getAllowScreenshots()` first; this exists for render-time reads after an
 * initial async load has populated the cache.
 */
export function getAllowScreenshotsSync(): boolean | null {
  return cached;
}

/**
 * Write the preference. Updates AsyncStorage, notifies all subscribers, and
 * (when running under Electron) pushes the value to the main process so the
 * window's content-protection is applied immediately.
 */
export async function setAllowScreenshots(value: boolean): Promise<void> {
  cached = value;
  try {
    await AsyncStorage.setItem(ALLOW_SCREENSHOTS_KEY, value ? 'true' : 'false');
  } catch {
    // Best-effort — if persistence fails, runtime listeners still see the new
    // value so the current session reflects the user's choice. Next launch
    // will fall back to the previously persisted value (or the false default).
  }
  const bridge = getElectronBridge();
  if (bridge) {
    try { await bridge.setAllowScreenshots?.(value); } catch {}
  }
  for (const listener of listeners) {
    try { listener(value); } catch {}
  }
}

/**
 * Subscribe to runtime changes. Returns an unsubscribe function. Listener is
 * called synchronously from `setAllowScreenshots`; it is NOT invoked with the
 * current value on subscribe (callers should `await getAllowScreenshots` for
 * initial state).
 */
export function subscribeAllowScreenshots(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Push the current AsyncStorage value to the Electron main process. Call once
 * at app startup (after the renderer mounts) so main's `preferences.json`
 * mirror catches up if the renderer's localStorage was the more recent writer.
 */
export async function syncToElectron(): Promise<void> {
  const bridge = getElectronBridge();
  if (!bridge) return;
  const value = await load();
  try { await bridge.setAllowScreenshots?.(value); } catch {}
}
